/**
 * Return-offer floating button — first-visit sessionStorage, PDP display, dynamic href.
 */

(function initCtrReturnOffer() {
  if (window.__ctrReturnOfferInitialized) return;
  window.__ctrReturnOfferInitialized = true;

  const DEFAULT_STORAGE_KEY = "ctrReturnOffer";

  function getRoot() {
    return document.querySelector("[data-ctr-return-offer]");
  }

  function parseConfig(root) {
    try {
      return JSON.parse(root.dataset.config || "{}");
    } catch (_e) {
      return {};
    }
  }

  function parseCommaList(value) {
    return String(value || "")
      .split(",")
      .map((item) => item.trim().toLowerCase())
      .filter(Boolean);
  }

  function isCheckoutPage() {
    return (
      window.location.pathname.includes("/checkouts/") ||
      Boolean(window.Shopify?.Checkout)
    );
  }

  function getPageContext(config) {
    const ctx = { ...(config.pageContext || {}) };
    ctx.isCheckout = Boolean(ctx.isCheckout) || isCheckoutPage();
    return ctx;
  }

  function isTriggerPage(offer, ctx) {
    const handles = parseCommaList(offer.triggerPageHandles);
    const suffixes = parseCommaList(offer.triggerTemplateSuffix);
    const pageHandle = String(ctx.pageHandle || "").toLowerCase();
    const templateSuffix = String(ctx.templateSuffix || "").toLowerCase();

    return (
      (pageHandle && handles.includes(pageHandle)) ||
      (templateSuffix && suffixes.includes(templateSuffix))
    );
  }

  function isDisplayPage(offer, ctx) {
    const types = parseCommaList(offer.displayPageTypes);
    const pageType = String(ctx.pageType || "").toLowerCase();
    return types.length === 0 || types.includes(pageType);
  }

  function parseHandleList(value) {
    if (Array.isArray(value)) {
      return value
        .map((item) => String(item).trim().toLowerCase())
        .filter(Boolean);
    }

    return parseCommaList(value);
  }

  function isDisplayProduct(offer, ctx) {
    const handles = parseHandleList(offer.displayProductHandles);
    if (handles.length === 0) return true;

    const pageType = String(ctx.pageType || "").toLowerCase();
    if (pageType !== "product") return true;

    const productHandle = String(ctx.productHandle || "").toLowerCase();
    return Boolean(productHandle) && handles.includes(productHandle);
  }

  function isAnyTriggerPage(offers, ctx) {
    return (offers || []).some((offer) => offer.enable && isTriggerPage(offer, ctx));
  }

  function applyQueryParams(url, offer) {
    const raw = String(offer?.queryParams || "").trim();

    if (raw) {
      const segments = raw.includes("\n") ? raw.split("\n") : raw.split("&");

      segments.forEach((segment) => {
        const trimmed = segment.trim();
        if (!trimmed) return;

        const eqIndex = trimmed.indexOf("=");
        if (eqIndex === -1) return;

        const key = trimmed.slice(0, eqIndex).trim();
        const value = trimmed.slice(eqIndex + 1).trim();

        if (key) {
          url.searchParams.set(key, value);
        }
      });

      return;
    }

    if (offer?.queryParamKey && offer?.queryParamValue) {
      url.searchParams.set(offer.queryParamKey, offer.queryParamValue);
    }
  }

  function buildHref(offer) {
    if (!offer?.destinationUrl) return "#";

    try {
      const url = new URL(offer.destinationUrl, window.location.origin);

      applyQueryParams(url, offer);

      const anchor = offer.buyboxAnchor || "";
      return `${url.pathname}${url.search}${anchor}`;
    } catch (_e) {
      return offer.destinationUrl;
    }
  }

  function getStorageKey(config) {
    return config.storageKey || DEFAULT_STORAGE_KEY;
  }

  function recordTriggerVisit(config) {
    const storageKey = getStorageKey(config);

    if (sessionStorage.getItem(storageKey)) return;

    const ctx = getPageContext(config);
    const matchedOffer = (config.offers || []).find(
      (offer) => offer.enable && isTriggerPage(offer, ctx)
    );

    if (!matchedOffer) return;

    sessionStorage.setItem(storageKey, JSON.stringify(matchedOffer));
  }

  function getStoredOffer(config) {
    const storageKey = getStorageKey(config);

    try {
      const raw = sessionStorage.getItem(storageKey);
      if (!raw) return null;

      const stored = JSON.parse(raw);
      const liveOffer = (config.offers || []).find(
        (offer) => offer.offerKey === stored.offerKey
      );

      if (!liveOffer?.enable) return null;

      return {
        ...stored,
        ...liveOffer,
      };
    } catch (_e) {
      return null;
    }
  }

  function shouldShowButton(config, offer) {
    const ctx = getPageContext(config);

    if (ctx.isCart || ctx.isCheckout) return false;
    if (!offer?.enable) return false;
    if (!isDisplayPage(offer, ctx)) return false;
    if (!isDisplayProduct(offer, ctx)) return false;
    if (isTriggerPage(offer, ctx)) return false;
    if (isAnyTriggerPage(config.offers || [], ctx)) return false;

    return true;
  }

  function resolveAnnouncementOffer(config) {
    const ctx = getPageContext(config);

    if (ctx.isCart || ctx.isCheckout) return null;

    const offers = config.offers || [];
    const pageTriggerOffer = offers.find(
      (offer) => offer.enable && isTriggerPage(offer, ctx)
    );

    if (pageTriggerOffer) {
      return pageTriggerOffer.announcementOverrideEnable ? pageTriggerOffer : null;
    }

    const storedOffer = getStoredOffer(config);

    if (storedOffer?.enable && storedOffer?.announcementOverrideEnable) {
      return storedOffer;
    }

    return null;
  }

  function getHeaderOffset() {
    const headerMini = document.querySelector(".header .header__mini");

    if (headerMini?.offsetHeight) {
      return headerMini.offsetHeight;
    }

    return headerMini?.classList.contains("is-collapsed") ? 44 : 56;
  }

  function scrollToBuyboxAnchor(anchor) {
    const id = String(anchor || "").replace(/^#/, "");
    if (!id) return false;

    const target = document.getElementById(id);

    if (!target) return false;

    const top = target.getBoundingClientRect().top + window.scrollY - getHeaderOffset();
    window.scrollTo({ top, behavior: "smooth" });
    return true;
  }

  function setAnnouncementEntryContent(entry, text, href) {
    if (!entry || !text) return;

    let link = entry.querySelector("a");

    if (!link) {
      const span = entry.querySelector("span");

      if (span) {
        link = document.createElement("a");
        link.href = href;
        link.textContent = text;
        span.replaceWith(link);
      }
    } else {
      link.textContent = text;
      link.href = href;
    }

    if (link) {
      link.dataset.ctrReturnOfferAnnouncementLink = "";
    }
  }

  function applyAnnouncementColors(bar, offer) {
    const bgColor = String(offer?.announcementBgColor || "").trim();
    const textColor = String(offer?.announcementTextColor || "").trim();

    if (bgColor) {
      bar.style.setProperty("--announcement-bar-background", bgColor);
      bar.style.background = bgColor;
    }

    if (textColor) {
      bar.style.color = textColor;
    }
  }

  function initReturnOfferAnnouncement(config) {
    const offer = resolveAnnouncementOffer(config);
    const bars = document.querySelectorAll("[data-ctr-return-offer-announcement]");

    if (!offer || !bars.length || !offer.announcementText) return;

    const ctx = getPageContext(config);
    const onTriggerLp = isTriggerPage(offer, ctx);
    const href = onTriggerLp ? offer.buyboxAnchor || "#" : buildHref(offer);
    const text = offer.announcementText || "";

    bars.forEach((bar) => {
      bar.classList.add("announcement-bar--return-offer");
      applyAnnouncementColors(bar, offer);
      bar.querySelectorAll(".announcement-bar-marquee__entry").forEach((entry) => {
        setAnnouncementEntryContent(entry, text, href);
      });
    });
  }

  function onAnnouncementClick(event) {
    const link = event.target.closest("[data-ctr-return-offer-announcement-link]");
    if (!link) return;

    const root = getRoot();
    if (!root) return;

    const config = parseConfig(root);
    const offer = resolveAnnouncementOffer(config);

    if (!offer) return;

    const ctx = getPageContext(config);

    if (!isTriggerPage(offer, ctx)) return;

    event.preventDefault();
    scrollToBuyboxAnchor(offer.buyboxAnchor);
  }

  function updateLabels(link, offer) {
    const mobileLabel = link.querySelector(".ctr-offcanvas-return-offer__label--mobile");
    const desktopLabel = link.querySelector(".ctr-offcanvas-return-offer__label--desktop");

    if (mobileLabel && offer.buttonLabelMobile) {
      mobileLabel.textContent = offer.buttonLabelMobile;
    }

    if (desktopLabel && offer.buttonLabelDesktop) {
      desktopLabel.textContent = offer.buttonLabelDesktop;
    }
  }

  function showLink(root, link, offer) {
    link.href = buildHref(offer);
    updateLabels(link, offer);
    link.removeAttribute("hidden");
    root.classList.add("is-visible");
  }

  function init() {
    const root = getRoot();
    if (!root) return;

    const config = parseConfig(root);

    recordTriggerVisit(config);

    const storedOffer = getStoredOffer(config);
    const link = root.querySelector("[data-ctr-return-offer-link]");

    if (storedOffer && link && shouldShowButton(config, storedOffer)) {
      showLink(root, link, storedOffer);
    }

    initReturnOfferAnnouncement(config);
  }

  document.addEventListener("click", onAnnouncementClick);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
